<template>
  <div class="course-offering-container container">
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <h2>إدارة المقررات الدراسية (إسناد المواد للفصول)</h2>
        </div>
      </template>

      <el-alert
        title="كيفية الاستخدام"
        type="info"
        description="1. استخدم الفلاتر لعرض الفصول. 2. اضغط على 'إدارة المواد' لأي فصل. 3. داخل النافذة، اختر القسم (الشعبة) ثم قم بإسناد المواد له."
        :closable="false"
        show-icon
        style="margin-bottom: 20px;"
      />

      <!-- --- قسم الفلاتر الجديد --- -->
      <el-form label-position="top">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="فلترة حسب السنة الدراسية">
              <el-select v-model="filters.study_year_id" placeholder="اختر السنة" @change="fetchClasses" clearable style="width: 100%;">
                <el-option v-for="year in studyYears" :key="year.id" :label="year.name" :value="year.id" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="فلترة حسب المستوى الدراسي">
              <el-select v-model="filters.grade_level_id" placeholder="اختر المستوى" @change="fetchClasses" clearable style="width: 100%;">
                <el-option v-for="level in gradeLevels" :key="level.id" :label="level.name" :value="level.id" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      
      <el-divider />

      <!-- --- الجدول المحدث --- -->
      <el-table :data="classes" v-loading="loading" border style="width: 100%">
        <el-table-column prop="name" label="اسم الفصل" />
        <el-table-column prop="grade_level.name" label="المستوى الدراسي" />
        <el-table-column label="الأقسام (الشُعب)" min-width="150">
          <template #default="{ row }">
            <el-tag
              v-for="section in row.sections"
              :key="section.id"
              size="small"
              style="margin-right: 5px;"
            >
              {{ section.name }}
            </el-tag>
            <span v-if="!row.sections || row.sections.length === 0">لا توجد أقسام</span>
          </template>
        </el-table-column>
        <el-table-column label="الإجراءات" width="180" fixed="right">
          <template #default="{ row }">
            <el-button size="small" type="primary" @click="handleManageOfferings(row)">
              <el-icon><Edit /></el-icon> إدارة المواد
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <div v-if="!loading && classes.length === 0">
        <el-empty description="الرجاء استخدام الفلاتر لعرض الفصول." />
      </div>

    </el-card>

    <!-- Dialog لإدارة المقررات -->
    <el-dialog v-model="dialogVisible" :title="`إدارة مواد الفصل: ${selectedClass?.name}`" width="70%">
      <div v-if="selectedClass">
        <el-form-item label="اختر القسم (الشعبة) لعرض مواده">
            <el-select v-model="selectedSection" placeholder="اختر القسم" @change="fetchAssignedSubjects" clearable style="width: 100%;">
                <el-option v-for="section in sections" :key="section.id" :label="section.name" :value="section.id" />
            </el-select>
        </el-form-item>

        <div v-if="selectedSection">
            <el-transfer
              v-model="assignedSubjectIds"
              :data="allSubjects"
              :titles="['المواد المتاحة', 'المواد المسندة لهذا القسم']"
              :props="{ key: 'id', label: 'name' }"
              filterable
              target-order="push"
              style="width: 100%; display: flex; justify-content: center;"
            />
        </div>
        <div v-else>
            <el-empty description="الرجاء اختيار القسم أولاً." />
        </div>
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">إلغاء</el-button>
          <el-button type="primary" @click="saveChanges" :loading="isSaving" :disabled="!selectedSection">
            <el-icon><Finished /></el-icon> حفظ التغييرات
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import { ElMessage } from 'element-plus';
import { Finished, Edit } from '@element-plus/icons-vue';

export default {
  name: 'CourseOfferingManagement',
  components: { Finished, Edit },
  data() {
    return {
      api: null,
      loading: false,
      isSaving: false,
      studyYears: [],
      gradeLevels: [], // --- الإضافة هنا ---
      classes: [],
      sections: [],
      allSubjects: [],
      assignedSubjectIds: [],
      selectedClass: null,
      selectedSection: null,
      dialogVisible: false,
      filters: { // --- الإضافة هنا ---
        study_year_id: null,
        grade_level_id: null,
      },
    };
  },
  created() {
    this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) config.headers['Authorization'] = `Bearer ${token}`;
      return config;
    });
    this.fetchInitialData();
  },
  methods: {
    async fetchInitialData() {
      try {
        const [yearsRes, subjectsRes, gradeLevelsRes] = await Promise.all([
          this.api.get('/study-years'),
          this.api.get('/subjects'),
          this.api.get('/grade-levels') // --- الإضافة هنا ---
        ]);
        this.studyYears = yearsRes.data.data || yearsRes.data;
        this.allSubjects = subjectsRes.data.data || subjectsRes.data;
        this.gradeLevels = gradeLevelsRes.data.data || gradeLevelsRes.data; // --- الإضافة هنا ---
      } catch (error) {
        ElMessage.error('فشل في جلب البيانات الأولية.');
      }
    },
    async fetchClasses() {
      if (!this.filters.study_year_id && !this.filters.grade_level_id) {
          this.classes = [];
          return;
      }
      this.loading = true;
      try {
        const response = await this.api.get('/classes', { params: this.filters });
        this.classes = response.data;
      } catch (error) {
        ElMessage.error('فشل في جلب الفصول الدراسية.');
      } finally {
        this.loading = false;
      }
    },
    async handleManageOfferings(classRow) {
      this.selectedClass = classRow;
      this.selectedSection = null;
      this.assignedSubjectIds = [];
      this.sections = [];
      try {
        const response = await this.api.get(`/classes/${this.selectedClass.id}/sections-for-assign`);
        this.sections = response.data;
        this.dialogVisible = true;
      } catch (error) {
        ElMessage.error('فشل في جلب أقسام هذا الفصل.');
      }
    },
    async fetchAssignedSubjects() {
        if (!this.selectedSection) {
            this.assignedSubjectIds = [];
            return;
        }
        try {
            const response = await this.api.get(`/classes/${this.selectedClass.id}/course-offerings`, {
                params: { 
                    study_year_id: this.filters.study_year_id,
                    section_id: this.selectedSection
                }
            });
            this.assignedSubjectIds = response.data.map(offering => offering.subject.id);
        } catch (error) {
            ElMessage.error('فشل في جلب المواد المسندة لهذا القسم.');
        }
    },
    async saveChanges() {
      if (!this.selectedClass || !this.filters.study_year_id || !this.selectedSection) return;
      this.isSaving = true;
      try {
        await this.api.post(`/classes/${this.selectedClass.id}/course-offerings`, {
          study_year_id: this.filters.study_year_id,
          section_id: this.selectedSection,
          subject_ids: this.assignedSubjectIds,
        });
        ElMessage.success('تم تحديث المقررات الدراسية للقسم بنجاح!');
        this.dialogVisible = false;
      } catch (error) {
        ElMessage.error(error.response?.data?.message || 'فشلت عملية الحفظ.');
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>



<style scoped>
.course-offering-container { padding: 20px; }
.card-header { text-align: center; }
.el-transfer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
:deep(.el-transfer-panel) {
    width: 300px;
    height: 400px;
}
:deep(.el-transfer-panel__body) {
    height: 350px;
}
</style>
